import * as React from "react";
import { useState } from "react";
import { Field, withTypes } from "react-final-form";
import axios from "axios";
import {
  Button,
  CardActions,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useNotify } from "react-admin";
import NavigateBeforeIcon from "@material-ui/icons/NavigateBefore";
import NavigateNextIcon from "@material-ui/icons/NavigateNext";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import moment from "moment";
import { ENDPOINTS } from "../apiConstants";
import { getConfigGet, getConfigPost } from "../getConfig";

const useStyles = makeStyles((theme) => ({
  main: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-start",
  },
  form: {
    padding: "0 1em 1em 1em",
    width: "100%",
    maxWidth: 520,
    margin: "0 auto",
  },
  input: {
    marginTop: "1em",
    width: "100%",
  },
  actions: {
    padding: "0 1em 1em 1em",
    width: "100%",
    maxWidth: 520,
    margin: "0 auto",
  },
  section: {
    width: "100%",
    maxWidth: 1200,
    margin: "1em auto",
    padding: "0 2em",
    boxSizing: "border-box",
  },
  sectionTitle: {
    fontWeight: 700,
    fontSize: "1.1rem",
    margin: "1em 0 0.5em",
  },
  sectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  headerControls: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  tableWrap: {
    overflowX: "auto",
  },
  headerSelect: {
    minWidth: 140,
  },
  tableCell: {
    padding: "4px 10px",
    border: "1px solid #ddd",
    fontSize: "0.85rem",
  },
  tableCellHead: {
    padding: "6px 10px",
    border: "1px solid #ddd",
    fontWeight: 700,
    fontSize: "0.85rem",
    backgroundColor: "#f5f5f5",
  },
  statusChip: {
    fontWeight: 600,
    fontSize: "0.75rem",
  },
  paginationRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    padding: "8px 0",
    gap: "8px",
  },
  rowClickable: {
    cursor: "pointer",
    "&:hover": { backgroundColor: "#fafafa" },
  },
  // ── detail ─────────────────────────────────────────────────
  detailCard: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2.5),
    width: "100%",
    boxSizing: "border-box",
  },
  detailHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: theme.spacing(2.5),
    gap: theme.spacing(2),
    flexWrap: "wrap",
  },
  detailTitle: {
    fontWeight: 700,
    fontSize: "1.25rem",
  },
  detailSubtitle: {
    color: theme.palette.text.secondary,
    fontSize: "0.85rem",
  },
  detailRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    columnGap: theme.spacing(3),
    rowGap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    width: "100%",
    boxSizing: "border-box",
  },
  statsRow: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    columnGap: theme.spacing(2),
    padding: theme.spacing(2, 0),
    marginBottom: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  statBlock: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
  },
  statValue: {
    fontSize: "1.35rem",
    fontWeight: 700,
    color: theme.palette.text.primary,
    lineHeight: 1.2,
  },
  statLabel: {
    fontSize: "0.7rem",
    fontWeight: 600,
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    letterSpacing: "0.07em",
    marginTop: 4,
  },
  subHeader: {
    fontSize: "0.75rem",
    fontWeight: 700,
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  detailField: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
  },
  detailLabel: {
    fontSize: "0.7rem",
    fontWeight: 600,
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    letterSpacing: "0.07em",
    marginBottom: 2,
  },
  detailValue: {
    fontSize: "0.9rem",
    color: theme.palette.text.primary,
    wordBreak: "break-word",
  },
  actionDivider: {
    borderTop: `1px solid ${theme.palette.divider}`,
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(2),
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
  },
  backBtn: {
    marginBottom: theme.spacing(1.5),
  },
  filterChip: {
    marginLeft: theme.spacing(1),
  },
}));

const renderInput = ({
  meta: { touched, error } = { touched: false, error: undefined },
  input: { ...inputProps },
  ...props
}: any) => (
  <TextField
    error={!!(touched && error)}
    helperText={touched && error}
    {...inputProps}
    {...props}
    fullWidth
  />
);

interface FormValues {
  EmailOrId?: string;
}
const { Form } = withTypes<FormValues>();

const STATUS_OPTIONS = ["", "active", "paused", "cancelled"];
const SCHEDULES_PAGE_SIZE = 10;
const CARDS_PAGE_SIZE = 5;
const TXNS_PAGE_SIZE = 10;
const AUDITS_PAGE_SIZE = 20;
const DAYS = ["", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const fmtDate = (v: string | null | undefined) =>
  v ? moment(v).format("YYYY-MM-DD HH:mm") : "-";
const fmtMoney = (n: number | null | undefined, cur = "GBP") =>
  n == null ? "-" : `${cur === "GBP" ? "£" : `${cur} `}${Number(n).toFixed(2)}`;

const statusColor = (s: string): "default" | "primary" | "secondary" => {
  if (s === "active" || s === "success") return "primary";
  if (s === "paused" || s === "pending" || s === "processing") return "default";
  return "secondary";
};

const renderRewards = (rewards: any[] | undefined) => {
  if (!rewards || rewards.length === 0) return "—";
  return rewards.map((r) => `${fmtMoney(r.amount)} (${r.transaction_status})`).join(", ");
};

const buildUserParams = (raw: string): any => {
  const trimmed = raw.trim();
  if (!trimmed) return {};
  const isEmail = isNaN(Number(trimmed));
  return isEmail ? { email: trimmed } : { user_id: trimmed };
};

const GiftCardTopupsDashboard = () => {
  const classes = useStyles();
  const notify = useNotify();

  const [view, setView] = useState<"list" | "detail">("list");
  const [emailOrId, setEmailOrId] = useState("");
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const [schedules, setSchedules] = useState<any>(null);
  const [schedulesPage, setSchedulesPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("active");

  const [txns, setTxns] = useState<any>(null);
  const [txnsPage, setTxnsPage] = useState(1);
  const [txnsLoading, setTxnsLoading] = useState(false);
  const [purchaseFilter, setPurchaseFilter] = useState<number | null>(null);

  const [cards, setCards] = useState<any[] | null>(null);
  const [cardsEmailOnly, setCardsEmailOnly] = useState(false);
  const [cardsPage, setCardsPage] = useState(1);
  const [cardsPagination, setCardsPagination] = useState<any>(null);
  const [cardsDeletedFilter, setCardsDeletedFilter] = useState("false");
  const [cardDelete, setCardDelete] = useState<any>(null);
  const [cardDeleting, setCardDeleting] = useState(false);

  const [detail, setDetail] = useState<any>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [audits, setAudits] = useState<any>(null);
  const [auditsPage, setAuditsPage] = useState(1);
  const [auditsLoading, setAuditsLoading] = useState(false);

  const [cardTxns, setCardTxns] = useState<any>(null);
  const [cardTxnsPage, setCardTxnsPage] = useState(1);
  const [cardTxnsLoading, setCardTxnsLoading] = useState(false);

  const [balance, setBalance] = useState<{
    loading: boolean;
    value: number | null;
    unavailable: boolean;
  }>({ loading: false, value: null, unavailable: false });

  const handleError = (err: any, fallback: string) => {
    notify(err?.response?.data?.message || err?.message || fallback, "error");
  };

  const loadSchedules = async (raw: string, page: number, status: string) => {
    const params: any = {
      ...buildUserParams(raw),
      page,
      pageSize: SCHEDULES_PAGE_SIZE,
    };
    if (status) params.status = status;
    const res: any = await axios.get(
      ENDPOINTS.ADMIN_SMART_TOPUPS,
      getConfigGet(params)
    );
    setSchedules(res.data);
    setSchedulesPage(page);
  };

  const loadTxns = async (
    raw: string,
    page: number,
    purchaseId: number | null
  ) => {
    setTxnsLoading(true);
    try {
      const params: any = {
        ...buildUserParams(raw),
        page,
        pageSize: TXNS_PAGE_SIZE,
      };
      if (purchaseId != null) params.purchase_id = purchaseId;
      const res: any = await axios.get(
        ENDPOINTS.ADMIN_GIFT_CARDS_TOPUP_TRANSACTIONS,
        getConfigGet(params)
      );
      setTxns(res.data);
      setTxnsPage(page);
    } catch (e) {
      handleError(e, "Failed to load top-up transactions");
    } finally {
      setTxnsLoading(false);
    }
  };

  // GIFT_CARD_DETAILS_PAGE resolves the user by email only, so this section is
  // unavailable when the admin searched by user id.
  const loadCards = async (raw: string, page: number, deleted = cardsDeletedFilter) => {
    const trimmed = raw.trim();
    if (!trimmed.includes("@")) {
      setCardsEmailOnly(true);
      setCards(null);
      setCardsPagination(null);
      setCardsPage(1);
      return;
    }
    setCardsEmailOnly(false);
    try {
      const res: any = await axios.get(
        ENDPOINTS.GIFT_CARD_DETAILS_PAGE,
        getConfigGet({
          email: trimmed,
          purchase_type: "top_up",
          is_deleted: deleted,
          page,
          limit: CARDS_PAGE_SIZE,
        })
      );
      // One payment row per reload, so collapse to the parent card.
      const rows = res.data?.data || [];
      const byPurchase = new Map<number, any>();
      rows.forEach((r: any) => {
        const pid = r.gift_card_purchase_id;
        if (pid != null && !byPurchase.has(pid)) byPurchase.set(pid, r);
      });
      setCards(Array.from(byPurchase.values()));
      // The API paginates payment rows and returns {total, limit, skip};
      // renderPagination wants the smart-topups shape.
      const total = res.data?.total || 0;
      const totalPages = Math.max(1, Math.ceil(total / CARDS_PAGE_SIZE));
      setCardsPagination({
        page,
        total,
        total_pages: totalPages,
        has_previous_page: page > 1,
        has_next_page: page < totalPages,
      });
      setCardsPage(page);
    } catch (e) {
      handleError(e, "Failed to load reloadable cards");
      setCards(null);
      setCardsPagination(null);
    }
  };

  const onCardsPage = (page: number) => loadCards(emailOrId, page);

  const deleteReloadableCard = async () => {
    if (!cardDelete?.gift_card_purchase_id || !cardDelete?.user_id) return;
    setCardDeleting(true);
    try {
      await axios.post(
        ENDPOINTS.CHANGE_GIFT_CARD_STATE,
        {
          user_id: cardDelete.user_id,
          purchase_id: cardDelete.gift_card_purchase_id,
          isDelete: "true",
        },
        { headers: getConfigPost() }
      );
      notify("Card deleted and smart top-up cancelled", "info");
      setCardDelete(null);
      // Under the Active filter the row leaves the list, which can empty the
      // last page.
      const wasLastOnPage = (cards?.length ?? 0) <= 1 && cardsPage > 1;
      await loadCards(emailOrId, wasLastOnPage ? cardsPage - 1 : cardsPage);
    } catch (e) {
      handleError(e, "Failed to delete card");
    } finally {
      setCardDeleting(false);
    }
  };

  const handleSubmit = async (data: FormValues) => {
    if (!data.EmailOrId) {
      notify("Please enter a user ID or email", "error");
      return;
    }
    setEmailOrId(data.EmailOrId);
    setLoading(true);
    setSearched(true);
    setPurchaseFilter(null);
    try {
      await Promise.all([
        loadSchedules(data.EmailOrId, 1, statusFilter),
        loadTxns(data.EmailOrId, 1, null),
        loadCards(data.EmailOrId, 1),
      ]);
    } catch (e) {
      handleError(e, "Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const onSchedulesPage = async (page: number) => {
    setLoading(true);
    try {
      await loadSchedules(emailOrId, page, statusFilter);
    } catch (e) {
      handleError(e, "Failed to load schedules");
    } finally {
      setLoading(false);
    }
  };

  const onTxnsPage = (page: number) => loadTxns(emailOrId, page, purchaseFilter);

  const filterByPurchase = (purchaseId: number | null) => {
    setPurchaseFilter(purchaseId);
    loadTxns(emailOrId, 1, purchaseId);
  };

  const openDetail = async (s: any) => {
    setView("detail");
    setDetailLoading(true);
    try {
      const params = buildUserParams(emailOrId);
      const [detailRes, auditRes] = await Promise.all([
        axios.get(
          `${ENDPOINTS.ADMIN_SMART_TOPUPS}/${s.id}`,
          getConfigGet(params)
        ),
        axios.get(
          ENDPOINTS.ADMIN_SMART_TOPUPS_AUDITS,
          getConfigGet({
            ...params,
            smart_topup_id: s.id,
            page: 1,
            pageSize: AUDITS_PAGE_SIZE,
          })
        ),
      ]);
      const detailData = (detailRes as any).data;
      setDetail(detailData);
      setAudits((auditRes as any).data);
      setAuditsPage(1);
      setBalance({ loading: false, value: null, unavailable: false });
      if (detailData?.purchase_id != null) {
        loadCardTxns(detailData.purchase_id, 1);
      } else {
        setCardTxns(null);
      }
    } catch (e) {
      handleError(e, "Failed to load schedule detail");
      setView("list");
    } finally {
      setDetailLoading(false);
    }
  };

  const onAuditsPage = async (page: number) => {
    if (!detail) return;
    setAuditsLoading(true);
    try {
      const params = {
        ...buildUserParams(emailOrId),
        smart_topup_id: detail.id,
        page,
        pageSize: AUDITS_PAGE_SIZE,
      };
      const res: any = await axios.get(
        ENDPOINTS.ADMIN_SMART_TOPUPS_AUDITS,
        getConfigGet(params)
      );
      setAudits(res.data);
      setAuditsPage(page);
    } catch (e) {
      handleError(e, "Failed to load audits");
    } finally {
      setAuditsLoading(false);
    }
  };

  const loadCardTxns = async (purchaseId: number, page: number) => {
    setCardTxnsLoading(true);
    try {
      const params: any = {
        ...buildUserParams(emailOrId),
        purchase_id: purchaseId,
        page,
        pageSize: TXNS_PAGE_SIZE,
      };
      const res: any = await axios.get(
        ENDPOINTS.ADMIN_GIFT_CARDS_TOPUP_TRANSACTIONS,
        getConfigGet(params)
      );
      setCardTxns(res.data);
      setCardTxnsPage(page);
    } catch (e) {
      handleError(e, "Failed to load card top-up transactions");
    } finally {
      setCardTxnsLoading(false);
    }
  };

  const onCardTxnsPage = (page: number) => {
    if (detail?.purchase_id != null) loadCardTxns(detail.purchase_id, page);
  };

  const checkBalance = async () => {
    if (!detail?.purchase_id || !detail?.user_id) return;
    setBalance({ loading: true, value: null, unavailable: false });
    try {
      const res: any = await axios.get(
        ENDPOINTS.ADMIN_GIFT_CARD_BALANCE_CHECK,
        getConfigGet({
          gift_card_purchase_id: detail.purchase_id,
          user_id: detail.user_id,
        })
      );
      const result = res.data?.data?.[0] || res.data?.[0] || res.data;
      if (result?.card_balance != null) {
        setBalance({
          loading: false,
          value: Number(result.card_balance),
          unavailable: false,
        });
      } else {
        setBalance({ loading: false, value: null, unavailable: true });
      }
    } catch (e) {
      setBalance({ loading: false, value: null, unavailable: true });
      handleError(e, "Balance check failed");
    }
  };

  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const deleteCard = async () => {
    if (!detail?.purchase_id || !detail?.user_id) return;
    setDeleting(true);
    try {
      await axios.post(
        ENDPOINTS.CHANGE_GIFT_CARD_STATE,
        {
          user_id: detail.user_id,
          purchase_id: detail.purchase_id,
          isDelete: "true",
        },
        { headers: getConfigPost() }
      );
      notify("Card deleted and smart top-up cancelled", "info");
      setDeleteConfirm(false);
      openDetail(detail);
    } catch (e) {
      handleError(e, "Failed to delete card");
    } finally {
      setDeleting(false);
    }
  };

  const backToList = () => {
    setView("list");
    setDetail(null);
    setAudits(null);
    setAuditsPage(1);
    setCardTxns(null);
    setCardTxnsPage(1);
    setBalance({ loading: false, value: null, unavailable: false });
  };

  const renderPagination = (
    pagination: any,
    page: number,
    onChange: (p: number) => void
  ) =>
    pagination ? (
      <div className={classes.paginationRow}>
        <Typography variant="body2">
          Page {pagination.page} of {pagination.total_pages} ({pagination.total} total)
        </Typography>
        <IconButton
          size="small"
          disabled={!pagination.has_previous_page}
          onClick={() => onChange(page - 1)}
        >
          <NavigateBeforeIcon />
        </IconButton>
        <IconButton
          size="small"
          disabled={!pagination.has_next_page}
          onClick={() => onChange(page + 1)}
        >
          <NavigateNextIcon />
        </IconButton>
      </div>
    ) : null;

  // ── DETAIL VIEW ──────────────────────────────────────────────
  if (view === "detail") {
    return (
      <div className={classes.main}>
        <div className={classes.section}>
          <Button
            className={classes.backBtn}
            startIcon={<ArrowBackIcon />}
            onClick={backToList}
          >
            Back to List
          </Button>

          {detailLoading ? (
            <div style={{ textAlign: "center", padding: 32 }}>
              <CircularProgress />
            </div>
          ) : !detail ? (
            <Typography>No data found.</Typography>
          ) : (
            <>
              <Paper className={classes.detailCard}>
                <div className={classes.detailHeader}>
                  <div>
                    <div className={classes.detailTitle}>
                      {detail.brand}
                    </div>
                    <div className={classes.detailSubtitle}>
                      Schedule #{detail.id} · User #{detail.user_id} · Created{" "}
                      {fmtDate(detail.created_at)} · Updated{" "}
                      {fmtDate(detail.updated_at)}
                    </div>
                  </div>
                  <Chip
                    label={detail.status}
                    color={statusColor(detail.status)}
                    size="small"
                    className={classes.statusChip}
                  />
                </div>

                <div className={classes.statsRow}>
                  <div className={classes.statBlock}>
                    <div className={classes.statValue}>{fmtMoney(detail.amount)}</div>
                    <div className={classes.statLabel}>Top-up Amount</div>
                  </div>
                  <div className={classes.statBlock}>
                    <div className={classes.statValue}>
                      {DAYS[detail.day_of_week] || detail.day_of_week}
                    </div>
                    <div className={classes.statLabel}>Day of Week</div>
                  </div>
                  <div className={classes.statBlock}>
                    <div className={classes.statValue}>
                      {detail.next_run_date || "—"}
                    </div>
                    <div className={classes.statLabel}>Next Run</div>
                  </div>
                  <div className={classes.statBlock}>
                    <div
                      className={classes.statValue}
                      style={{
                        color: detail.failure_streak > 0 ? "#d32f2f" : undefined,
                      }}
                    >
                      {detail.failure_streak}
                    </div>
                    <div className={classes.statLabel}>Failure Streak</div>
                  </div>
                </div>

                <div className={classes.subHeader}>Schedule</div>
                <div className={classes.detailRow}>
                  <div className={classes.detailField}>
                    <span className={classes.detailLabel}>Threshold %</span>
                    <span className={classes.detailValue}>
                      {detail.threshold_pct ?? "—"}
                    </span>
                  </div>
                  <div className={classes.detailField}>
                    <span className={classes.detailLabel}>Last Run ID</span>
                    <span className={classes.detailValue}>
                      {detail.last_run_id ?? "—"}
                    </span>
                  </div>
                  <div className={classes.detailField}>
                    <span className={classes.detailLabel}>Paused / Cancelled</span>
                    <span className={classes.detailValue}>
                      {detail.paused_at
                        ? `Paused ${fmtDate(detail.paused_at)}`
                        : detail.cancelled_at
                        ? `Cancelled ${fmtDate(detail.cancelled_at)}`
                        : "—"}
                    </span>
                  </div>
                </div>

                <div className={classes.subHeader}>Parent Card</div>
                <div className={classes.detailRow}>
                  <div className={classes.detailField}>
                    <span className={classes.detailLabel}>Purchase ID</span>
                    <span className={classes.detailValue}>
                      {detail.purchase_id ?? "(first-run pending)"}
                    </span>
                  </div>
                  <div className={classes.detailField}>
                    <span className={classes.detailLabel}>Card URL</span>
                    <span className={classes.detailValue}>
                      {detail.purchase?.url ? (
                        <a
                          href={detail.purchase.url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          View
                        </a>
                      ) : (
                        "—"
                      )}
                    </span>
                  </div>
                  <div className={classes.detailField}>
                    <span className={classes.detailLabel}>Current Balance</span>
                    <span className={classes.detailValue}>
                      {balance.loading ? (
                        <CircularProgress size={16} />
                      ) : balance.value != null ? (
                        <Chip
                          size="small"
                          label={fmtMoney(balance.value)}
                          style={{
                            backgroundColor: "#e8f5e9",
                            color: "#2e7d32",
                            fontWeight: 600,
                          }}
                        />
                      ) : balance.unavailable ? (
                        <span style={{ color: "#999" }}>Unavailable</span>
                      ) : detail.purchase?.has_balance_check &&
                        detail.purchase_id != null ? (
                        <Button
                          size="small"
                          variant="outlined"
                          style={{ color: "#2e7d32", borderColor: "#2e7d32" }}
                          onClick={checkBalance}
                        >
                          Check balance
                        </Button>
                      ) : (
                        "—"
                      )}
                    </span>
                  </div>
                </div>

                {detail.purchase_id != null && detail.status !== "cancelled" && (
                  <div className={classes.actionDivider}>
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={() => setDeleteConfirm(true)}
                    >
                      Delete Card
                    </Button>
                  </div>
                )}
              </Paper>

              <Dialog open={deleteConfirm} onClose={() => setDeleteConfirm(false)}>
                <DialogTitle>Delete this top-up card?</DialogTitle>
                <DialogContent>
                  <DialogContentText>
                    This soft-deletes the {detail.brand} card (purchase #
                    {detail.purchase_id}) and cancels its smart top-up schedule.
                    The cron will stop topping it up.
                  </DialogContentText>
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setDeleteConfirm(false)} disabled={deleting}>
                    Cancel
                  </Button>
                  <Button
                    color="secondary"
                    variant="contained"
                    onClick={deleteCard}
                    disabled={deleting}
                  >
                    {deleting ? <CircularProgress size={20} /> : "Delete Card"}
                  </Button>
                </DialogActions>
              </Dialog>

              <Typography variant="subtitle1" className={classes.sectionTitle}>
                Recent runs (last 5)
              </Typography>
              <Paper>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell className={classes.tableCellHead}>ID</TableCell>
                      <TableCell className={classes.tableCellHead}>Decision</TableCell>
                      <TableCell className={classes.tableCellHead}>Run Date</TableCell>
                      <TableCell className={classes.tableCellHead}>Run Ended</TableCell>
                      <TableCell className={classes.tableCellHead}>Balance Before</TableCell>
                      <TableCell className={classes.tableCellHead}>Threshold</TableCell>
                      <TableCell className={classes.tableCellHead}>Payment</TableCell>
                      <TableCell className={classes.tableCellHead}>Top-up</TableCell>
                      <TableCell className={classes.tableCellHead}>Reason</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(detail.recent_runs || []).map((r: any) => (
                      <TableRow key={r.id}>
                        <TableCell className={classes.tableCell}>{r.id}</TableCell>
                        <TableCell className={classes.tableCell}>
                          {r.decision || "—"}
                        </TableCell>
                        <TableCell className={classes.tableCell}>
                          {r.run_date || "—"}
                        </TableCell>
                        <TableCell className={classes.tableCell}>
                          {r.run_ended_at ? fmtDate(r.run_ended_at) : "—"}
                        </TableCell>
                        <TableCell className={classes.tableCell}>
                          {r.balance_at_check != null ? fmtMoney(r.balance_at_check) : "—"}
                        </TableCell>
                        <TableCell className={classes.tableCell}>
                          {r.threshold_amount != null ? fmtMoney(r.threshold_amount) : "—"}
                        </TableCell>
                        <TableCell className={classes.tableCell}>
                          {r.gift_card_payment_id ?? "—"}
                        </TableCell>
                        <TableCell className={classes.tableCell}>
                          {r.gift_card_topup_id ?? "—"}
                        </TableCell>
                        <TableCell className={classes.tableCell}>
                          {r.reason || "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                    {(!detail.recent_runs || detail.recent_runs.length === 0) && (
                      <TableRow>
                        <TableCell
                          className={classes.tableCell}
                          colSpan={9}
                          align="center"
                        >
                          No runs yet
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Paper>

              {detail.purchase_id != null && (
                <>
                  <div className={classes.sectionHeader} style={{ marginTop: "1em" }}>
                    <Typography variant="subtitle1" className={classes.sectionTitle}>
                      Top-up Transactions for this card{" "}
                      {cardTxns?.pagination
                        ? `(${cardTxns.pagination.total} total)`
                        : ""}
                    </Typography>
                    {cardTxnsLoading && <CircularProgress size={18} />}
                  </div>
                  <Paper>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell className={classes.tableCellHead}>ID</TableCell>
                          <TableCell className={classes.tableCellHead}>Amount</TableCell>
                          <TableCell className={classes.tableCellHead}>Status</TableCell>
                          <TableCell className={classes.tableCellHead}>Reference</TableCell>
                          <TableCell className={classes.tableCellHead}>Payment ID</TableCell>
                          <TableCell className={classes.tableCellHead}>Reward</TableCell>
                          <TableCell className={classes.tableCellHead}>Created</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {cardTxns?.data?.map((t: any) => (
                          <TableRow key={t.id}>
                            <TableCell className={classes.tableCell}>{t.id}</TableCell>
                            <TableCell className={classes.tableCell}>
                              {fmtMoney(t.face_value_amount, t.face_value_currency)}
                            </TableCell>
                            <TableCell className={classes.tableCell}>
                              <Chip
                                label={t.status}
                                color={statusColor(t.status)}
                                size="small"
                                className={classes.statusChip}
                              />
                            </TableCell>
                            <TableCell className={classes.tableCell}>
                              {t.reference || "—"}
                            </TableCell>
                            <TableCell className={classes.tableCell}>
                              {t.transaction_id ?? "—"}
                            </TableCell>
                            <TableCell className={classes.tableCell}>
                              {renderRewards(t.rewards)}
                            </TableCell>
                            <TableCell className={classes.tableCell}>
                              {fmtDate(t.created_at)}
                            </TableCell>
                          </TableRow>
                        ))}
                        {(!cardTxns?.data || cardTxns.data.length === 0) &&
                          !cardTxnsLoading && (
                            <TableRow>
                              <TableCell
                                className={classes.tableCell}
                                colSpan={7}
                                align="center"
                              >
                                No top-up transactions for this card
                              </TableCell>
                            </TableRow>
                          )}
                      </TableBody>
                    </Table>
                  </Paper>
                  {renderPagination(
                    cardTxns?.pagination,
                    cardTxnsPage,
                    onCardTxnsPage
                  )}
                </>
              )}

              <div className={classes.sectionHeader} style={{ marginTop: "1em" }}>
                <Typography variant="subtitle1" className={classes.sectionTitle}>
                  Audit log{" "}
                  {audits?.pagination ? `(${audits.pagination.total} total)` : ""}
                </Typography>
                {auditsLoading && <CircularProgress size={18} />}
              </div>
              <Paper>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell className={classes.tableCellHead}>When</TableCell>
                      <TableCell className={classes.tableCellHead}>Action</TableCell>
                      <TableCell className={classes.tableCellHead}>Actor</TableCell>
                      <TableCell className={classes.tableCellHead}>Previous</TableCell>
                      <TableCell className={classes.tableCellHead}>New</TableCell>
                      <TableCell className={classes.tableCellHead}>Notes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {audits?.data?.map((a: any) => (
                      <TableRow key={a.id}>
                        <TableCell className={classes.tableCell}>
                          {fmtDate(a.created_at)}
                        </TableCell>
                        <TableCell className={classes.tableCell}>{a.action}</TableCell>
                        <TableCell className={classes.tableCell}>
                          {a.actor}
                          {a.actor_user_id ? ` (${a.actor_user_id})` : ""}
                        </TableCell>
                        <TableCell className={classes.tableCell}>
                          <code style={{ fontSize: "0.75rem" }}>
                            {a.previous_value ? JSON.stringify(a.previous_value) : "—"}
                          </code>
                        </TableCell>
                        <TableCell className={classes.tableCell}>
                          <code style={{ fontSize: "0.75rem" }}>
                            {a.new_value ? JSON.stringify(a.new_value) : "—"}
                          </code>
                        </TableCell>
                        <TableCell className={classes.tableCell}>
                          {a.notes || "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                    {(!audits?.data || audits.data.length === 0) && !auditsLoading && (
                      <TableRow>
                        <TableCell
                          className={classes.tableCell}
                          colSpan={6}
                          align="center"
                        >
                          No audit events
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Paper>
              {renderPagination(audits?.pagination, auditsPage, onAuditsPage)}
            </>
          )}
        </div>
      </div>
    );
  }

  // ── LIST VIEW ────────────────────────────────────────────────
  return (
    <div className={classes.main}>
      <div style={{ width: "100%", maxWidth: 520, marginTop: "2em" }}>
        <Typography
          variant="h5"
          style={{ textAlign: "center", marginBottom: "1em" }}
        >
          Top Ups
        </Typography>
        <Form
          onSubmit={handleSubmit}
          render={({ handleSubmit }) => (
            <form onSubmit={handleSubmit} noValidate>
              <div className={classes.form}>
                <div className={classes.input}>
                  <Field
                    autoFocus
                    name="EmailOrId"
                    component={renderInput}
                    label="User ID or Email"
                    disabled={loading}
                  />
                </div>
                <div className={classes.input}>
                  <Select
                    fullWidth
                    displayEmpty
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value as string)}
                  >
                    {STATUS_OPTIONS.map((s) => (
                      <MenuItem key={s || "any"} value={s}>
                        {s || "Any status"}
                      </MenuItem>
                    ))}
                  </Select>
                </div>
              </div>
              <CardActions className={classes.actions}>
                <Button
                  variant="contained"
                  type="submit"
                  color="primary"
                  disabled={loading}
                  fullWidth
                >
                  {loading ? <CircularProgress size={25} thickness={2} /> : "Search"}
                </Button>
              </CardActions>
            </form>
          )}
        />
      </div>

      {searched && schedules && (
        <div className={classes.section}>
          <Typography className={classes.sectionTitle}>
            Top-up Schedules ({schedules.pagination?.total || 0} total)
          </Typography>
          <Paper className={classes.tableWrap}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell className={classes.tableCellHead}>ID</TableCell>
                  <TableCell className={classes.tableCellHead}>Brand</TableCell>
                  <TableCell className={classes.tableCellHead}>Amount</TableCell>
                  <TableCell className={classes.tableCellHead}>Day</TableCell>
                  <TableCell className={classes.tableCellHead}>Status</TableCell>
                  <TableCell className={classes.tableCellHead}>Next Run</TableCell>
                  <TableCell className={classes.tableCellHead}>Failure Streak</TableCell>
                  <TableCell className={classes.tableCellHead}>Purchase ID</TableCell>
                  <TableCell className={classes.tableCellHead}>Card URL</TableCell>
                  <TableCell className={classes.tableCellHead}>Updated</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {schedules.data?.map((s: any) => (
                  <TableRow
                    key={s.id}
                    className={classes.rowClickable}
                    onClick={() => openDetail(s)}
                  >
                    <TableCell className={classes.tableCell}>{s.id}</TableCell>
                    <TableCell className={classes.tableCell}>{s.brand}</TableCell>
                    <TableCell className={classes.tableCell}>
                      {fmtMoney(s.amount)}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {DAYS[s.day_of_week] || s.day_of_week}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      <Chip
                        label={s.status}
                        color={statusColor(s.status)}
                        size="small"
                        className={classes.statusChip}
                      />
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {s.next_run_date || "—"}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {s.failure_streak}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {s.purchase_id ?? "—"}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {s.purchase?.url ? (
                        <a
                          href={s.purchase.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                        >
                          View
                        </a>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {fmtDate(s.updated_at)}
                    </TableCell>
                  </TableRow>
                ))}
                {(!schedules.data || schedules.data.length === 0) && (
                  <TableRow>
                    <TableCell
                      className={classes.tableCell}
                      colSpan={10}
                      align="center"
                    >
                      No schedules found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
          {renderPagination(schedules.pagination, schedulesPage, onSchedulesPage)}
        </div>
      )}

      {searched && (cards || cardsEmailOnly) && (
        <div className={classes.section}>
          <div className={classes.sectionHeader}>
            <Typography className={classes.sectionTitle}>
              Reloadable Cards{cards ? ` (${cards.length})` : ""}
            </Typography>
            <div className={classes.headerControls}>
              <TextField
                select
                label="Show"
                value={cardsDeletedFilter}
                onChange={(e) => {
                  const v = e.target.value as string;
                  setCardsDeletedFilter(v);
                  loadCards(emailOrId, 1, v);
                }}
                disabled={cardsEmailOnly}
                className={classes.headerSelect}
              >
                <MenuItem value="false">Active</MenuItem>
                <MenuItem value="true">Deleted</MenuItem>
                <MenuItem value="all">All</MenuItem>
              </TextField>
            </div>
          </div>
          {cardsEmailOnly ? (
            <Paper style={{ padding: 16 }}>
              <Typography variant="body2">
                Search by email to view and manage reloadable cards.
              </Typography>
            </Paper>
          ) : (
            <Paper className={classes.tableWrap}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.tableCellHead}>
                      Purchase ID
                    </TableCell>
                    <TableCell className={classes.tableCellHead}>Brand</TableCell>
                    <TableCell className={classes.tableCellHead}>
                      Purchase Status
                    </TableCell>
                    <TableCell className={classes.tableCellHead}>Card URL</TableCell>
                    <TableCell className={classes.tableCellHead}>Created</TableCell>
                    <TableCell className={classes.tableCellHead}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cards?.map((c: any) => (
                    <TableRow key={c.gift_card_purchase_id}>
                      <TableCell className={classes.tableCell}>
                        {c.gift_card_purchase_id}
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        {c.brand_name || "—"}
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        <Chip
                          label={c.purchase_status || "—"}
                          color={statusColor(c.purchase_status || "")}
                          size="small"
                          className={classes.statusChip}
                        />
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        {c.url ? (
                          <a href={c.url} target="_blank" rel="noopener noreferrer">
                            View
                          </a>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        {fmtDate(c.payment_created_date)}
                      </TableCell>
                      <TableCell className={classes.tableCell}>
                        {c.purchase_is_deleted ? (
                          <Chip
                            label="deleted"
                            size="small"
                            className={classes.statusChip}
                          />
                        ) : (
                          <Button
                            size="small"
                            variant="outlined"
                            color="secondary"
                            onClick={() => setCardDelete(c)}
                          >
                            Delete
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {(!cards || cards.length === 0) && (
                    <TableRow>
                      <TableCell
                        className={classes.tableCell}
                        colSpan={6}
                        align="center"
                      >
                        No reloadable cards
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Paper>
          )}
          {!cardsEmailOnly &&
            renderPagination(cardsPagination, cardsPage, onCardsPage)}
        </div>
      )}

      <Dialog open={Boolean(cardDelete)} onClose={() => setCardDelete(null)}>
        <DialogTitle>Delete this top-up card?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This soft-deletes the {cardDelete?.brand_name} card (purchase #
            {cardDelete?.gift_card_purchase_id}) and cancels its smart top-up
            schedule. The cron will stop topping it up.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCardDelete(null)} disabled={cardDeleting}>
            Cancel
          </Button>
          <Button
            color="secondary"
            variant="contained"
            onClick={deleteReloadableCard}
            disabled={cardDeleting}
          >
            {cardDeleting ? <CircularProgress size={20} /> : "Delete Card"}
          </Button>
        </DialogActions>
      </Dialog>

      {searched && txns && (
        <div className={classes.section}>
          <div className={classes.sectionHeader}>
            <Typography className={classes.sectionTitle}>
              Top-up Transactions ({txns.pagination?.total || 0} total)
              {purchaseFilter != null && (
                <Chip
                  size="small"
                  className={classes.filterChip}
                  label={`purchase_id=${purchaseFilter}`}
                  onDelete={() => filterByPurchase(null)}
                />
              )}
            </Typography>
            {txnsLoading && <CircularProgress size={20} />}
          </div>
          <Paper className={classes.tableWrap}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell className={classes.tableCellHead}>ID</TableCell>
                  <TableCell className={classes.tableCellHead}>Brand</TableCell>
                  <TableCell className={classes.tableCellHead}>Amount</TableCell>
                  <TableCell className={classes.tableCellHead}>Status</TableCell>
                  <TableCell className={classes.tableCellHead}>Reference</TableCell>
                  <TableCell className={classes.tableCellHead}>Payment ID</TableCell>
                  <TableCell className={classes.tableCellHead}>Parent Purchase</TableCell>
                  <TableCell className={classes.tableCellHead}>Card URL</TableCell>
                  <TableCell className={classes.tableCellHead}>Reward</TableCell>
                  <TableCell className={classes.tableCellHead}>Created</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {txns.data?.map((t: any) => (
                  <TableRow key={t.id}>
                    <TableCell className={classes.tableCell}>{t.id}</TableCell>
                    <TableCell className={classes.tableCell}>{t.brand}</TableCell>
                    <TableCell className={classes.tableCell}>
                      {fmtMoney(t.face_value_amount, t.face_value_currency)}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      <Chip
                        label={t.status}
                        color={statusColor(t.status)}
                        size="small"
                        className={classes.statusChip}
                      />
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {t.reference || "—"}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {t.transaction_id ?? "—"}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      <Button
                        size="small"
                        onClick={() => filterByPurchase(t.purchase_id)}
                      >
                        {t.purchase_id}
                      </Button>
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {t.parent_url ? (
                        <a
                          href={t.parent_url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          View
                        </a>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {renderRewards(t.rewards)}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {fmtDate(t.created_at)}
                    </TableCell>
                  </TableRow>
                ))}
                {(!txns.data || txns.data.length === 0) && (
                  <TableRow>
                    <TableCell
                      className={classes.tableCell}
                      colSpan={10}
                      align="center"
                    >
                      No top-up transactions
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
          {renderPagination(txns.pagination, txnsPage, onTxnsPage)}
        </div>
      )}
    </div>
  );
};

export default GiftCardTopupsDashboard;
